import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePetPolicies } from '../../../../hooks/usePetPolicies';

import clockIcon from '../../../../assets/policy/info-circle.svg';

import { currencyFormat } from '../../../../services/utils';

import { ColumnWrapper } from './ColumnWrapper';
import { RowWrapper } from './RowWrapper';
import { CellWithTitle } from './CellWithTitle';
import { WaitingPeriodRow } from './WaitingPeriodRow';
import { CoveragePPUInfoFeeShimmer } from './CoveragePPUInfoFeeShimmer';

import { formatShortDateByLanguage } from '../../../../services/language.utils';
import { MARKETING_CHANNEL_ID } from '../../../../theme/theme.constants';

import styles from '../css/CoveragePPUInfoFee.module.css';

const CoveragePPUInfoFee = ({
  marketingChanneld = MARKETING_CHANNEL_ID.ppu,
  selectedPolicy = {},
}) => {
  const { t } = useTranslation('insurance');
  const petPolicies = usePetPolicies();
  const { loading } = petPolicies;

  const formatDate = (date) => formatShortDateByLanguage(date);

  const policyLimit = useMemo(() => {
    if (selectedPolicy?.PolicyDisplayNumber && selectedPolicy?.Benefit) {
      const benefit = selectedPolicy?.Benefit
        .replace('K', ',000')
        .replace('$', '');

      // currency format for policy not for language
      // TODO: currency should come from the backend pending
      return t('coverageOverviewPPU.limitPerTerm', {
        benefit: currencyFormat({ value: benefit }),
      });
    }

    return t('coverageOverviewPPU.na');
  }, [selectedPolicy, t]);

  const hideMemberId = useMemo(() => {
    const brandsToHide = [
      MARKETING_CHANNEL_ID.ppuPetCare,
      MARKETING_CHANNEL_ID.spotCan,
    ];

    return brandsToHide.includes(marketingChanneld);
  }, [marketingChanneld]);

  const isSpotCanada = useMemo(
    () => marketingChanneld === MARKETING_CHANNEL_ID.spotCan,
    [marketingChanneld],
  );

  if (loading) {
    return <CoveragePPUInfoFeeShimmer />;
  }

  return (
    <div className={styles.mainContainer}>
      <ColumnWrapper>
        <RowWrapper>
          <CellWithTitle
            description={selectedPolicy.PolicyDisplayNumber}
            title={t('coverageOverviewPPU.policyNumber')}
          />

          {hideMemberId ? (
            <CellWithTitle
              description={selectedPolicy.ProductName} // TBC
              title={t('coverageOverviewPPU.policyName')}
            />
          ) : (
            <CellWithTitle
              description={selectedPolicy.Id} // TBC
              title={t('coverageOverviewPPU.memberId')}
            />
          )}
        </RowWrapper>

        <RowWrapper>
          {hideMemberId ? null : (
            <CellWithTitle
              description={selectedPolicy.ProductName} // TBC
              title={t('coverageOverviewPPU.policyName')}
            />
          )}

          <CellWithTitle
            description={policyLimit} // TBC
            title={t('coverageOverviewPPU.policyLimit')}
          />

          <CellWithTitle
            description={`${formatDate(selectedPolicy.PolicyStartDate)} - 
              ${formatDate(selectedPolicy.PolicyEndDate)}`}
            title={t('coverageOverviewPPU.policyTerm')}
          />
        </RowWrapper>
      </ColumnWrapper>

      <div className={styles.waitingPeriodContainer}>
        <div className={styles.waitingPeriodsTitleContainer}>
          <h1>{t('coverageOverviewPPU.waitingPeriod')}</h1>

          <img alt="" src={clockIcon} />
        </div>

        {!isSpotCanada && (
          <WaitingPeriodRow
            description={t('coverageOverviewPPU.accidentBenefitsSchedule')}
            title={t('coverageOverviewPPU.accidentBenefits')}
          />
        )}

        <WaitingPeriodRow
          description={t('coverageOverviewPPU.illnessBenefitsSchedule')}
          title={t('coverageOverviewPPU.illnessBenefits')}
        />

        {!isSpotCanada && (
          <WaitingPeriodRow
            description={t('coverageOverviewPPU.additionalBenefitsSchedule')}
            title={t('coverageOverviewPPU.additionalBenefits')}
          />
        )}
      </div>
    </div>
  );
};

export { CoveragePPUInfoFee };
